import { useState } from "react";
import { Btn, Dot } from "../ui";
import { requestPasswordReset, signIn, signUp } from "../auth-client";
import { LegacyMark } from "../LegacyMark";
import { ResetPassword } from "./ResetPassword";

function hasResetQuery(): boolean {
  const params = new URLSearchParams(window.location.search);
  return Boolean(params.get("token") || params.get("error"));
}

function clearResetQuery(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete("token");
  url.searchParams.delete("error");
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, "", next);
}

export function Login({
  signupEnabled,
  onSuccess,
}: {
  signupEnabled: boolean;
  onSuccess: () => void;
}) {
  const [mode, setMode] = useState<"signin" | "signup" | "forgot">("signin");
  const [showReset, setShowReset] = useState(() => hasResetQuery());
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  if (showReset) {
    return (
      <ResetPassword
        onBackToLogin={() => {
          clearResetQuery();
          setShowReset(false);
          setMode("signin");
          setErr(null);
        }}
      />
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      if (mode === "forgot") {
        const res = await requestPasswordReset({ email, redirectTo: "/" });
        if (res.error) {
          setErr(res.error.message ?? "Invio non riuscito");
          return;
        }
        setForgotSent(true);
        return;
      }
      if (mode === "signin") {
        const res = await signIn.email({ email, password });
        if (res.error) {
          setErr(res.error.message ?? "Credenziali non valide");
          return;
        }
      } else {
        const res = await signUp.email({ email, password, name });
        if (res.error) {
          setErr(res.error.message ?? "Registrazione fallita");
          return;
        }
      }
      onSuccess();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-brand">
          <LegacyMark className="login-mark" />
          <span className="brand-wordmark">Legacy</span>
        </div>
        <h1 className="login-title">
          {mode === "signin" ? "Accedi" : mode === "signup" ? "Registrati" : "Password dimenticata"}
        </h1>
        <div className="login-sub">
          {mode === "signin"
            ? "Accedi con email e password per entrare nella console"
            : mode === "signup"
              ? "Crea un account per accedere alla console"
              : "Inserisci l'email dell'account. Se esiste, ti invieremo un link per reimpostare la password."}
        </div>

        <form onSubmit={submit} className="login-form">
          {mode === "signup" && (
            <div className="field">
              <label className="label" htmlFor="login-name">Nome</label>
              <input
                id="login-name"
                className="input"
                type="text"
                required
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Mario Rossi"
              />
            </div>
          )}
          <div className="field">
            <label className="label" htmlFor="login-email">Email</label>
            <input
              id="login-email"
              className="input"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@azienda.it"
            />
          </div>
          {mode !== "forgot" && (
            <div className="field">
              <label className="label" htmlFor="login-password">Password</label>
              <input
                id="login-password"
                className="input"
                type="password"
                required
                minLength={8}
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "signup" ? "min. 8 caratteri" : "••••••••••"}
              />
            </div>
          )}

          {mode === "signin" && (
            <div style={{ textAlign: "right", marginTop: -6 }}>
              <a
                href="#"
                style={{ color: "var(--accent)", fontSize: 13, textDecoration: "none", fontWeight: 500 }}
                onClick={(e) => {
                  e.preventDefault();
                  setMode("forgot");
                  setErr(null);
                  setForgotSent(false);
                }}
              >
                Password dimenticata?
              </a>
            </div>
          )}

          {err && (
            <div className="login-err" role="alert">
              <Dot kind="danger" />
              <span>{err}</span>
            </div>
          )}

          {mode === "forgot" && forgotSent && (
            <div className="login-err" role="status" style={{ background: "color-mix(in oklab, var(--accent) 12%, transparent)", borderColor: "var(--accent-line)", color: "var(--accent)" }}>
              <Dot kind="live" />
              <span>Se l'account esiste, riceverai un'email con il link per reimpostare la password.</span>
            </div>
          )}

          <Btn variant="primary" block disabled={busy}>
            {busy ? "…" : mode === "signin" ? "Accedi" : mode === "signup" ? "Crea account" : "Invia link"}
          </Btn>
        </form>

        {(signupEnabled || mode === "forgot") && (
          <div className="login-switch">
            {mode === "forgot" ? (
              <>
                Ricordi la password?{" "}
                <a href="#" onClick={(e) => { e.preventDefault(); setMode("signin"); setErr(null); setForgotSent(false); }}>
                  Accedi
                </a>
              </>
            ) : mode === "signin" ? (
              <>
                Non hai un account?{" "}
                <a href="#" onClick={(e) => { e.preventDefault(); setMode("signup"); setErr(null); }}>
                  Registrati
                </a>
              </>
            ) : (
              <>
                Hai già un account?{" "}
                <a href="#" onClick={(e) => { e.preventDefault(); setMode("signin"); setErr(null); }}>
                  Accedi
                </a>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
